import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { modeLinkCounts, MODE_CATEGORY_HEX, type ModeEntry } from '@/lib/modes';
import { AdminPage, Pill, FeedbackControl } from '@/admin';

function RegistryCard({
  title, primary, secondary, to, muted,
}: {
  title: string; primary: string; secondary?: string; to?: string; muted?: string;
}) {
  const body = (
    <div className={`h-full rounded border border-border bg-surface p-21 ${to ? 'transition-colors hover:border-border-strong hover:bg-surface-sunken' : ''}`}>
      <div className="text-[12px] font-semibold uppercase tracking-wide text-text-secondary">{title}</div>
      <div className="mt-2 text-h2 font-semibold">{primary}</div>
      {secondary ? <div className="mt-1 text-body-sm text-text-secondary">{secondary}</div> : null}
      {to ? <div className="mt-3 text-body-sm text-accent">Open →</div> : muted ? <div className="mt-3 text-body-sm text-text-muted">{muted}</div> : null}
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-21">
      <h2 className="mb-8 text-h3 font-semibold">{title}</h2>
      {children}
    </section>
  );
}

export function ModeDetailPage() {
  const { modeId = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['mode', modeId], queryFn: () => adminApi.modes.get(modeId) });
  const fb = useQuery({ queryKey: ['feedback-summary', 'modes'], queryFn: () => feedbackApi.summary('modes') });
  const sbFb = useQuery({ queryKey: ['feedback-summary', 'mode_sub_branches'], queryFn: () => feedbackApi.summary('mode_sub_branches') });

  const mode = (q.data?.mode ?? {}) as ModeEntry;
  const c = modeLinkCounts(mode);
  const refreshModes = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', 'modes'] });
  const refreshSb = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', 'mode_sub_branches'] });

  const panelsSecondary = [
    `${c.panelsRequired} required · ${c.panelsRecommended} recommended`,
    c.panelsSexSpecific ? `+${c.panelsSexSpecific} sex-specific` : '',
  ].filter(Boolean).join(' ');

  return (
    <AdminPage
      title={mode.display_name || modeId}
      description={mode.description}
      backTo={{ to: '/admin/modes', label: 'All modes' }}
    >
      {q.isLoading ? <p className="text-body-sm text-text-muted">Loading…</p> : null}
      {q.isError ? <p className="text-body-sm text-flagged">Could not load this mode.</p> : null}

      {q.data ? (
        <>
          <div className="mb-21 flex items-center gap-8">
            {mode.category ? <Pill label={mode.category} color={MODE_CATEGORY_HEX[mode.category]} /> : null}
            <span className="font-mono text-[12px] text-text-muted">{modeId}</span>
            {mode.active_sub_branch ? <span className="text-body-sm text-text-secondary">active sub-branch: <strong>{mode.active_sub_branch}</strong></span> : null}
            <FeedbackControl registry="modes" itemId={modeId} itemLabel={mode.display_name || modeId} openCount={fb.data?.[modeId]?.open ?? 0} onChanged={refreshModes} />
          </div>

          {/* Registries index */}
          <div className="grid grid-cols-2 gap-13 md:grid-cols-3">
            <RegistryCard title="Panels" primary={`${c.panelsRequired + c.panelsRecommended + c.panelsSexSpecific}`} secondary={panelsSecondary} to={`/admin/modes/${encodeURIComponent(modeId)}/panels`} />
            <RegistryCard title="Indicated compounds" primary={`${c.compounds}`} secondary="Compound registry" to="/admin/compounds" />
            <RegistryCard title="Contraindications" primary={`${c.contraindications}`} secondary="Compound safety" to={c.contraindications ? `/admin/modes/${encodeURIComponent(modeId)}/contraindications` : undefined} muted={c.contraindications ? undefined : 'none'} />
            <RegistryCard title="Monitoring & dose" primary={`${c.monitoring + c.doseMods}`} secondary={`${c.monitoring} monitoring · ${c.doseMods} dose mods`} />
            <RegistryCard title="Sub-branches" primary={`${c.subBranches}`} secondary="Clinical lenses" muted={c.subBranches ? 'see below' : 'none'} />
            <RegistryCard title="Tasks" primary={`${c.tasksRequired + c.tasksRecommended}`} secondary={`${c.tasksRequired} required · ${c.tasksRecommended} recommended`} to={`/admin/modes/${encodeURIComponent(modeId)}/tasks`} />
          </div>

          {/* Sub-branches (clinical lenses) */}
          {mode.sub_branches && Object.keys(mode.sub_branches).length ? (
            <Section title="Sub-branches">
              <div className="divide-y divide-border rounded border border-border bg-surface">
                {Object.entries(mode.sub_branches).map(([sbId, sb]) => (
                  <div key={sbId} className="flex items-start justify-between gap-13 p-13">
                    <div className="min-w-0">
                      <div className="flex items-center gap-8">
                        <span className="font-semibold">{sb.display_name || sbId}</span>
                        <span className="font-mono text-[11px] text-text-muted">{sbId}</span>
                        {mode.active_sub_branch === sbId ? <span className="rounded-pill bg-success-soft px-2 py-0.5 text-[10px] font-semibold text-success">active</span> : null}
                      </div>
                      {sb.interpretive_lens ? <p className="mt-1 max-w-[640px] text-body-sm text-text-secondary">{sb.interpretive_lens.length > 240 ? `${sb.interpretive_lens.slice(0, 239)}…` : sb.interpretive_lens}</p> : null}
                    </div>
                    <FeedbackControl registry="mode_sub_branches" itemId={`${modeId}:${sbId}`} itemLabel={`${mode.display_name || modeId} · ${sb.display_name || sbId}`} openCount={sbFb.data?.[`${modeId}:${sbId}`]?.open ?? 0} onChanged={refreshSb} />
                  </div>
                ))}
              </div>
            </Section>
          ) : null}

          {/* Directives */}
          {(mode.interpretive_lens || mode.synthesiser_addendum || mode.critic_addenda) ? (
            <Section title="Board directives">
              <div className="space-y-8 rounded border border-border bg-surface p-21 text-body-sm">
                {mode.interpretive_lens ? <div><span className="font-semibold">Interpretive lens:</span> <span className="text-text-secondary">{mode.interpretive_lens}</span></div> : null}
                {mode.synthesiser_addendum ? <div><span className="font-semibold">Synthesiser addendum:</span> <span className="text-text-secondary">{mode.synthesiser_addendum}</span></div> : null}
                {mode.critic_addenda ? <div><span className="font-semibold">Critic addenda:</span> <span className="text-text-secondary">present</span></div> : null}
              </div>
            </Section>
          ) : null}
        </>
      ) : null}
    </AdminPage>
  );
}
